/**
 * showTypes — SOURCE UNIQUE des types d'événement et de leur profil (2026-10-17).
 *
 * POURQUOI : les 8 onglets de la page show et le bloc venue s'affichaient pour
 * TOUS les types — un travel day ou un day off n'ont ni venue ni rider technique.
 * Chaque type déclare ici un PROFIL : `needsVenue` (masque le bloc venue sans
 * jamais détruire de données) et `tabs` (les onglets qui ont du sens).
 *
 * RÈGLE DE SÉCURITÉ : on MASQUE des champs, on ne détruit jamais de données —
 * changer le type d'un show ne supprime rien ; remettre le type réaffiche tout.
 *
 * Consumé par : ShowDetailPage (venue block + onglets + placeholder du nom),
 * ShowsImportReviewPage (liste des types), et les tests.
 */

export const EVENT_TYPE_OPTIONS = [
  'show', 'travel', 'soundcheck', 'rehearsal', 'festival', 'djset',
  'promo', 'studio', 'off',
  // LOT 4 UX (2026-11-04) : `other` = « Boarding / On-site (no show) » — la
  // valeur SQL est DÉJÀ autorisée par le CHECK de tour_dates (oct. 2026), on
  // l'EXPOSE donc ici sans jamais toucher au schéma (règle AGENTS.md 5).
  'other',
] as const;

export type ShowEventType = (typeof EVENT_TYPE_OPTIONS)[number] | '';

/**
 * Clés de masquage de la DAY SHEET (LOT 4, 2026-11-04) — une clé = un champ de
 * `GENERAL INFORMATION`, masqué pour un type d'événement donné. Le masquage
 * n'est JAMAIS destructeur : un champ déjà rempli reste affiché (règle d'or,
 * `isDaySheetFieldShown`).
 */
export type DaySheetHiddenKey =
  | 'doors' | 'runningOrder' | 'stageName' | 'guests' | 'artistPass'
  | 'passport' | 'duration' | 'wifi' | 'electricity' | 'luggage' | 'currency';

/** Champ de la day sheet (colonne de tour_dates) → clé de masquage. */
const FIELD_HIDDEN_KEY: Record<string, DaySheetHiddenKey> = {
  door_time: 'doors',
  running_order: 'runningOrder',
  stage_name: 'stageName',
  artist_pass: 'artistPass',
  passport_reminder: 'passport',
  duration_minutes: 'duration',
  wifi_venue: 'wifi',
  electricity: 'electricity',
  luggage_notes: 'luggage',
  currency_note: 'currency',
  currency_rate: 'currency',
};

/** Les onglets de la page show, dans l'ordre d'affichage. */
export type ShowTab =
  | 'day' | 'logistics' | 'tech' | 'setlist' | 'checklist'
  | 'files' | 'advancing' | 'finance';

/** Profils par type — tout type inconnu/vide (`other`, `''`) reçoit le profil COMPLET. */
export interface ShowTypeProfile {
  /** Le bloc venue (sélecteur + recherche en ligne) a du sens pour ce type. */
  needsVenue: boolean;
  /** Onglets affichés pour ce type (les autres sont masqués, données intactes). */
  tabs: ShowTab[];
  /**
   * Le bloc PERSONNES : qui le show concerne, et comment le nommer.
   * performers = « qui joue » (hint rider + itinéraire) · travelers = « qui voyage » ·
   * involved = « qui est impliqué » (soundcheck/promo) · concerned = « qui est concerné ».
   */
  people: 'performers' | 'travelers' | 'involved' | 'concerned';
  /** Le sélecteur de setlist (header) n'a de sens que pour les types qui jouent. */
  hideSetlist?: boolean;
  /**
   * Blocs/champs de la DAY SHEET (ItinerarySection + DaysheetViewer + PDF) masqués
   * pour ce type — doors/running order/stage name n'ont aucun sens sur un travel day.
   */
  daySheetHidden?: DaySheetHiddenKey[];
  /** Placeholder du nom adapté au type (appliqué seulement si le nom est vide). */
  namePhKey?: string;
}

const ALL_TABS: ShowTab[] = ['day', 'logistics', 'tech', 'setlist', 'checklist', 'files', 'advancing', 'finance'];

// Spectacle : profil COMPLET (festival/djset sont des variantes de show).
const PERFORMANCE: ShowTypeProfile = { needsVenue: true, tabs: ALL_TABS, people: 'performers' };
// Journée sans spectacle : pas de venue, pas de rider, pas de setlist/advancing/finance.
// daySheetHidden (LOT 4, 2026-11-04) : on masque les champs de CONCERT
// (doors / running order / stage name / durée / wifi / artist pass / invités) —
// on GARDE ce qui sert vraiment sur la route : welcome, électricité (photos des
// prises), timezone, devise, météo, bagages, passeport.
const NO_SHOW: ShowTypeProfile = {
  needsVenue: false, tabs: ['day', 'logistics', 'checklist', 'files'],
  people: 'travelers', hideSetlist: true,
  daySheetHidden: ['doors', 'runningOrder', 'stageName', 'duration', 'wifi', 'artistPass', 'guests'],
};

const PROFILES: Record<string, ShowTypeProfile> = {
  show: PERFORMANCE,
  festival: PERFORMANCE,
  djset: PERFORMANCE,
  travel: { ...NO_SHOW, namePhKey: 'namePhTravel' },
  off: {
    needsVenue: false, tabs: ['day', 'checklist', 'files'],
    people: 'concerned', hideSetlist: true,
    // Day off : artist pass GARDÉ (festival / croisière), wifi masqué (pas de salle).
    daySheetHidden: ['doors', 'runningOrder', 'stageName', 'duration', 'wifi', 'guests'],
    namePhKey: 'namePhOff',
  },
  // Boarding / on-site without a show (LOT 4, 2026-11-04) : la salle / le
  // bateau / le terminal EST le lieu du jour — venue affichée, wifi et artist
  // pass utiles (on embarque), rien du concert (doors / ordre de passage /
  // scène / durée / invités).
  other: {
    needsVenue: true, tabs: ['day', 'logistics', 'checklist', 'files'],
    people: 'travelers', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName', 'duration', 'guests'],
    namePhKey: 'namePhTravel',
  },
  soundcheck: {
    needsVenue: true, tabs: ['day', 'logistics', 'files', 'checklist'],
    people: 'involved', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName'],
  },
  rehearsal: {
    needsVenue: true, tabs: ['day', 'logistics', 'files', 'checklist'],
    people: 'involved', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName'],
  },
  // Promo / press day : venue souvent oui (studio radio, salle d'interview) —
  // mais jamais de rider ni de setlist ni d'advancing ni de finance.
  promo: {
    needsVenue: true, tabs: ['day', 'logistics', 'checklist', 'files'],
    people: 'involved', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName', 'duration'],
  },
  // Writing / recording day : comme un day off avec un objectif.
  studio: {
    needsVenue: false, tabs: ['day', 'logistics', 'checklist', 'files'],
    people: 'concerned', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName', 'guests', 'duration', 'wifi', 'artistPass'],
    namePhKey: 'namePhStudio',
  },
};

/** Profil d'un type — TOUJOURS défini (type inconnu/vide = profil COMPLET, rétro-compatible). */
export function showTypeProfile(eventType: string | null | undefined): ShowTypeProfile {
  return PROFILES[(eventType ?? '').toLowerCase()] ?? PERFORMANCE;
}

/**
 * Profil EFFECTIF d'un show (LOT 4, 2026-11-04) : un travel day qui porte un
 * LIEU (`venue_id`) est en réalité une journée d'embarquement / d'arrivée sur
 * site — le bloc venue doit s'afficher même si le type reste `travel`.
 * Le type et les données restent inchangés (rien n'est réécrit en base).
 */
export function showTypeProfileFor(
  eventType: string | null | undefined,
  venueId?: string | null,
): ShowTypeProfile {
  const base = showTypeProfile(eventType);
  const type = (eventType ?? '').toLowerCase();
  if (type === 'travel' && String(venueId ?? '').trim()) {
    // Journée d'embarquement : le lieu est connu, donc le Wi-Fi ET l'artist pass
    // redeviennent utiles (on embarque à bord / sur site) — le reste du profil
    // travel (pas de doors / ordre de passage / scène / durée / invités) tient.
    const kept = new Set<DaySheetHiddenKey>(base.daySheetHidden ?? []);
    kept.delete('wifi');
    kept.delete('artistPass');
    return { ...base, needsVenue: true, daySheetHidden: [...kept] };
  }
  return base;
}

/**
 * Ce travel day est-il le RETOUR À LA MAISON (J4) ?
 * Deux signaux, tous deux posés par l'UI : la sentinelle `destination_show_id
 * === 'home'`, ou une ville d'arrivée égale à la ville de base du groupe
 * (`bandHomeCity`, Band Settings). Un travel day normal (J1, vers un autre
 * pays) n'est PAS un retour.
 */
export function isHomeReturnTravel(
  show: Record<string, any> | null | undefined,
  bandHomeCity?: string | null,
): boolean {
  if ((String(show?.event_type ?? '').toLowerCase()) !== 'travel') return false;
  if (String(show?.destination_show_id ?? '') === 'home') return true;
  const home = String(bandHomeCity ?? '').trim().toLowerCase();
  const to = String(show?.to_city ?? '').trim().toLowerCase();
  return !!home && !!to && home === to;
}

/**
 * Champs de la day sheet réellement masqués pour CE show (LOT 4, 2026-11-04).
 * Part du profil du type, puis un travel RETOUR ajoute électricité (photos des
 * prises), passeport et devise : on rentre chez soi, l'adaptateur et le
 * convertisseur ne servent plus. `luggage_notes` reste visible (quitter l'hôtel
 * / le bus sans rien oublier).
 */
export function effectiveDaySheetHidden(
  show: Record<string, any> | null | undefined,
  bandHomeCity?: string | null,
): Set<DaySheetHiddenKey> {
  // `showTypeProfileFor` : un travel qui porte un LIEU (embarquement) se comporte
  // comme une journée sur site — Wi-Fi et artist pass redeviennent utiles.
  const hidden = new Set<DaySheetHiddenKey>(
    showTypeProfileFor(show?.event_type, show?.venue_id).daySheetHidden ?? [],
  );
  if (isHomeReturnTravel(show, bandHomeCity)) {
    hidden.add('electricity');
    hidden.add('passport');
    hidden.add('currency');
  }
  return hidden;
}

/**
 * RÈGLE D'OR NON DESTRUCTRICE (LOT 4) : un champ masqué par le profil reste
 * affiché s'il porte DÉJÀ une valeur sur ce show — aucune donnée saisie dans le
 * passé n'est jamais cachée en silence. Utilisée par l'éditeur
 * (ItinerarySection), le viewer (DaysheetViewer) et le PDF (daysheet.ts).
 */
export function isDaySheetFieldShown(
  show: Record<string, any> | null | undefined,
  fieldKey: string,
  bandHomeCity?: string | null,
): boolean {
  const key = FIELD_HIDDEN_KEY[fieldKey];
  if (!key) return true;
  if (!effectiveDaySheetHidden(show, bandHomeCity).has(key)) return true;
  if (fieldKey === 'duration_minutes') return Number(show?.[fieldKey]) > 0;
  return String(show?.[fieldKey] ?? '').trim().length > 0;
}

/**
 * Ville d'ÉTAPE d'un show (LOT 4, 2026-11-04, décision user) :
 * on prend la ville de l'HÔTEL quand un hôtel est renseigné, sinon celle de la
 * VENUE, sinon la ville du show. Sert à pré-remplir `from_city` / `to_city`
 * des travel days (7C) : on part de l'hôtel où l'on dort, pas de la salle.
 */
export function resolveShowStopCity(
  target:
    | { venue_id?: string | number | null; city?: string | null }
    | null
    | undefined,
  targetAccommodations?: Array<{ city?: string | null }> | null,
  venues?: Array<{ id: string | number; city?: string | null }> | null,
): string {
  const hotelCity = (targetAccommodations ?? [])
    .map((a) => String(a?.city ?? '').trim())
    .find((c) => !!c);
  if (hotelCity) return hotelCity;
  const v = (venues ?? []).find((x) => String(x.id) === String(target?.venue_id ?? ''));
  const venueCity = String(v?.city ?? '').trim();
  if (venueCity) return venueCity;
  return String(target?.city ?? '').trim();
}

/**
 * Nom AUTO-GÉNÉRÉ d'un travel day (2026-10-17, user) : `Travel Day (Genève) - (Milan)`.
 * ÉDITABLE : l'auto-nom ne s'applique que si le nom est VIDE ou correspond DÉJÀ au pattern
 * auto (un nom tapé à la main n'est jamais écrasé — on détecte le pattern via
 * `isAutoTravelName`).
 */
export function autoTravelName(fromCity?: string | null, toCity?: string | null): string {
  const from = (fromCity || '').trim();
  const to = (toCity || '').trim();
  if (from && to) return `Travel Day (${from}) - (${to})`;
  if (from) return `Travel Day (${from})`;
  return 'Travel Day';
}

const AUTO_TRAVEL_RE = /^Travel Day( \()?/i;
/** Le nom actuel est-il un auto-nom (safe à régénérer) ? Un nom manuel n'est jamais touché. */
export function isAutoTravelName(name?: string | null): boolean {
  const n = (name || '').trim();
  if (!n) return true; // vide → l'auto-nom peut s'appliquer
  return AUTO_TRAVEL_RE.test(n);
}

/**
 * Noms AUTO par type (2026-10-17, user) : travel = Travel Day (from) - (to) ;
 * soundcheck / rehearsal / promo = `{Type} — {venue}` quand la venue est connue.
 */
export function autoShowName(
  eventType: string | null | undefined,
  ctx: { fromCity?: string | null; toCity?: string | null; venueName?: string | null },
): string | null {
  const type = (eventType || '').toLowerCase();
  if (type === 'travel') return autoTravelName(ctx.fromCity, ctx.toCity);
  const v = (ctx.venueName || '').trim();
  if (!v) return null;
  if (type === 'soundcheck') return `Soundcheck — ${v}`;
  if (type === 'rehearsal') return `Rehearsal — ${v}`;
  if (type === 'promo') return `Promo — ${v}`;
  return null;
}

/**
 * LOT ATLAS-ICONS-AND-NAMES (2026-11-04) — icône + libellé d'un type
 * d'événement, SOURCE UNIQUE partagée par la liste Shows et la Route map.
 *
 * POURQUOI : la Route map n'affichait AUCUNE icône, donc deux dates sur la même
 * venue (dont la première est une EMBARKATION) se lisaient comme deux concerts
 * identiques. Un travel day porte désormais l'icône de son moyen de transport —
 * ou ⛴️ quand son nom parle d'embarquement (ferry / cruise / boarding).
 *
 * Fonction PURE : aucun accès réseau, aucun champ inventé (type vide → Show).
 */
export function showTypeIcon(s: {
  event_type?: string | null;
  destination_show_id?: string | number | null;
  destination_transport?: string | null;
  transport?: string | null;
  name?: string | null;
} | null | undefined): { icon: string; title: string } {
  const et = String(s?.event_type ?? '').toLowerCase();
  switch (et) {
    case 'show': case 'concert': return { icon: '🔊', title: 'Show' };
    case 'festival': return { icon: '🎪', title: 'Festival' };
    case 'djset': return { icon: '🎧', title: 'DJ set' };
    case 'soundcheck': return { icon: '🎛️', title: 'Soundcheck' };
    case 'rehearsal': return { icon: '📝', title: 'Rehearsal' };
    case 'interview': case 'promo': return { icon: '🎤', title: 'Promo / Interview' };
    case 'studio': return { icon: '🎙️', title: 'Studio' };
    case 'off': return { icon: '🚫', title: 'Day off' };
    case 'other': return { icon: '⚓', title: 'Boarding / On-site' };
    case 'travel': {
      // Retour à la maison : la sentinelle `destination_show_id = 'home'`.
      if (String(s?.destination_show_id ?? '') === 'home') {
        return { icon: '🏠', title: 'Travel · home' };
      }
      // Embarquement (ferry / croisière / navire) : le NOM porte le signal —
      // un travel day vers un bateau n'a pas de `destination_transport` utile.
      if (/embark|boarding|ferry|cruise|ship|boat/i.test(String(s?.name ?? ''))) {
        return { icon: '⛴️', title: 'Travel · embarkation' };
      }
      const tr = String(s?.destination_transport || s?.transport || '').toLowerCase();
      if (tr === 'plane') return { icon: '✈️', title: 'Travel · plane' };
      if (tr === 'tourbus') return { icon: '🚌', title: 'Travel · tour bus' };
      if (tr === 'train') return { icon: '🚆', title: 'Travel · train' };
      if (tr === 'van') return { icon: '🚐', title: 'Travel · van' };
      return { icon: '🗺️', title: 'Travel' };
    }
    default: return { icon: '🔊', title: s?.event_type || 'Show' };
  }
}

/**
 * Types qui ne sont PAS un concert : sur ces jours, le NOM de l'événement
 * (`tour_dates.name`, ex. « Hellsinki Metal Cruise — embarkation ») est le
 * titre de la journée, pas la salle.
 */
export const NON_SHOW_EVENT_TYPES = ['travel', 'other', 'off', 'studio', 'rehearsal', 'soundcheck', 'promo'] as const;

/**
 * Libellé principal d'une date (LOT ATLAS-ICONS-AND-NAMES, 2026-11-04).
 *
 * RÈGLE : `tour_dates.name` est le titre SPÉCIFIQUE du jour, `venues.name` la
 * salle. Afficher la salle d'abord effaçait « — embarkation » / « — <band> »
 * quand deux dates partageaient la même venue. On préfère donc :
 *   1. jour SANS concert → le nom de l'événement (c'est le sujet du jour) ;
 *   2. jour de concert → le nom de l'événement SEULEMENT s'il contient déjà le
 *      nom de la salle (`Hellsinki Metal Cruise — Samael` ⊃ `Hellsinki Metal
 *      Cruise`), sinon la salle (comportement historique de la liste) ;
 *   3. sinon la salle, la `venue_name` du show, puis `—`.
 */
export function showPrimaryLabel(
  show: {
    event_type?: string | null;
    name?: string | null;
    venue_name?: string | null;
  } | null | undefined,
  venueName?: string | null,
): string {
  const sName = String(show?.name ?? '').trim();
  const vName = String(venueName ?? show?.venue_name ?? '').trim();
  const type = String(show?.event_type ?? '').toLowerCase();
  if (sName) {
    const nonShow = (NON_SHOW_EVENT_TYPES as readonly string[]).includes(type);
    if (nonShow) return sName;
    if (!vName) return sName;
    if (sName.toLowerCase().includes(vName.toLowerCase())) return sName;
  }
  return vName || sName || '—';
}

/** Le nom est-il un AUTO-nom (safe à régénérer) ? Un nom manuel n'est jamais touché. */
export function isAutoShowName(name?: string | null): boolean {
  const n = (name || '').trim();
  if (!n) return true;
  if (AUTO_TRAVEL_RE.test(n)) return true;
  return [/^Soundcheck — /i, /^Rehearsal — /i, /^Promo — /i].some((re) => re.test(n));
}
